import React, { useEffect, useMemo, useState } from 'react';
import styled from 'astroturf/react';

import { useSidebarData } from 'js/liswp/Sidebar/hooks/useSidebarData';
import {
  SidebarSection,
  Work as SidebarWork,
} from 'js/liswp/Sidebar/hooks/types';
import { getWorksViews } from 'js/liswp/work_overview/api';

import { Shimmer } from 'js/liswp_blocks/shared/Shimmer';
import { authorLine } from 'js/liswp_blocks/shared/authorLine';
import { formatStat } from 'js/liswp_blocks/shared/formatStat';
import { syntheticRelevancy } from 'js/liswp_blocks/shared/relevancy';

/**
 * Shared PDF-pack contents preview for the block-page heroes (tabs,
 * pick_download): the pack's related papers as non-interactive rows —
 * thumbnail, title, "by A +2 more | 1.2K views" meta (phones drop the author
 * segment per the comps), and a rank-derived relevancy badge (above the title
 * on phones, at the row's right edge on desktop) — fading out at the bottom
 * into a "+N related research papers" count.
 *
 * Shared rather than forked per variant (liswp_blocks/AGENTS.md Rule 2); the
 * comps differ only in density, hence `variant`:
 *  - 'tabs' (2190:22910): self-padded 16×20 rows, 50×72 thumbs, semibold
 *    titles, 28px/14px badges, beige page outlines.
 *  - 'pickDownload' (2190:23032): 24px-gapped flush rows, 40×58 thumbs,
 *    regular titles, 22px/12px badges, light-grey outlines.
 *
 * The rows read the same /liswp_sidebar payload the rail uses (via
 * usePackPreviewWorks; no new endpoint) with one batched views request; the
 * relevancy percentage is synthetic (see ./relevancy). Rows are a
 * non-interactive preview — no links, so no impression/nav instrumentation.
 * The consumer owns the clipping container (fixed height + overflow hidden)
 * and the surrounding card chrome.
 */

export type PackPreviewVariant = 'tabs' | 'pickDownload';

/** The liswp_sidebar related works + one batched views request for the rows. */
export function usePackPreviewWorks(
  workId: number,
  maxRows = 6,
): { relatedWorks: SidebarWork[]; rowViews: Record<string, number> } {
  const { sidebarData } = useSidebarData(workId);
  const relatedWorks: SidebarWork[] = useMemo(
    () => (sidebarData?.[SidebarSection.RelatedWorks] ?? []).slice(0, maxRows),
    [sidebarData, maxRows],
  );

  const [rowViews, setRowViews] = useState<Record<string, number>>({});
  const relatedWorkIds = useMemo(
    () => relatedWorks.map((relatedWork) => relatedWork.id),
    [relatedWorks],
  );
  const relatedWorkIdsKey = relatedWorkIds.join(',');
  useEffect(() => {
    if (!relatedWorkIds.length) return;

    getWorksViews(relatedWorkIds)
      .then((response) => setRowViews(response.views))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the id list
  }, [relatedWorkIdsKey]);

  return { relatedWorks, rowViews };
}

const Rows = styled.div<{ variant: PackPreviewVariant }>`
  display: flex;
  flex-direction: column;
  width: 100%;

  &.variant-tabs {
    padding-top: var(--spacing-xs-8, 8px);
  }

  &.variant-pickDownload {
    gap: var(--spacing-sm-24, 24px);
    padding-bottom: var(--spacing-xs-16, 16px);
  }
`;

const Row = styled.div<{ variant: PackPreviewVariant }>`
  display: flex;
  gap: var(--spacing-xs-12, 12px);
  align-items: flex-start;
  width: 100%;

  &.variant-tabs {
    padding: var(--spacing-xs-16, 16px) 20px;
  }

  // Explicit no-op so astroturf consumes the prop value (unmatched enum
  // values are forwarded to the DOM as attributes).
  &.variant-pickDownload {
    padding: 0;
  }
`;

const RowThumb = styled.img<{ variant: PackPreviewVariant }>`
  flex-shrink: 0;
  object-fit: cover;
  object-position: top center;
  background: var(--background-white, #ffffff);

  &.variant-tabs {
    width: 50px;
    height: 72px;
    border: 1px solid var(--stroke-beige-light, #ccc9bc);
  }

  &.variant-pickDownload {
    width: 40px;
    height: 58px;
    border: 1px solid var(--stroke-light, #dddde2);
  }
`;

const RowBody = styled.div`
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  gap: var(--spacing-xs-4, 4px);
  min-width: 0;
`;

// Row title, clamped to two lines. tabs uses body/small-bold, pick_download
// body/small regular, per the comps.
const RowTitle = styled.p<{ variant: PackPreviewVariant }>`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin: 0;
  font-family:
    var(--type-font-family-sans-serif, 'DM Sans'), Helvetica, Arial, sans-serif;
  font-size: var(--type-body-sm-size, 14px);
  line-height: var(--type-body-sm-line-height, 20px);
  color: var(--text-primary, #141413);

  &.variant-tabs {
    font-weight: var(--type-body-sm-bold-font-weight, 600);
  }

  &.variant-pickDownload {
    font-weight: var(--type-body-sm-font-weight, 400);
  }
`;

const RowMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-xs-8, 8px);
  font-family:
    var(--type-font-family-sans-serif, 'DM Sans'), Helvetica, Arial, sans-serif;
  font-size: var(--type-body-xs-size, 12px);
  line-height: var(--type-body-xs-line-height, 16px);
  color: var(--text-secondary, #535366);
`;

// The comps' phone rows drop the author segment (views only); desktop keeps
// "by A +2 more |".
const RowMetaAuthor = styled.span`
  @import 'stylesheets/design_system/breakpoint';

  display: inline-flex;
  gap: var(--spacing-xs-8, 8px);

  @include breakpoint(small, medium) {
    display: none;
  }
`;

// The "98% relevancy" badge (lime fill + success outline). Desktop keeps it at
// the row's right edge; phones move it above the title.
const RelevancyBadge = styled.span<{
  variant: PackPreviewVariant;
  placement: 'desktop' | 'mobile';
}>`
  @import 'stylesheets/design_system/breakpoint';

  align-items: center;
  background: var(--brand-colors-expanded-colors-lime-100, #f2f5ef);
  border: 1px solid var(--brand-colors-success-900, #035c0f);
  border-radius: var(--border-radius-radius-md, 8px);
  font-family:
    var(--type-font-family-sans-serif, 'DM Sans'), Helvetica, Arial, sans-serif;
  color: var(--brand-colors-expanded-colors-lime-900, #333e2c);
  white-space: nowrap;

  &.variant-tabs {
    height: 28px;
    padding: 0 var(--spacing-xs-8, 8px);
    font-size: var(--type-body-sm-size, 14px);
    line-height: var(--type-body-sm-line-height, 20px);
  }

  &.variant-pickDownload {
    height: 22px;
    padding: 0 6px;
    font-size: var(--type-body-xs-size, 12px);
    line-height: var(--type-body-xs-line-height, 16px);
    font-weight: var(--type-body-xs-bold-font-weight, 600);
  }

  &.placement-desktop {
    display: inline-flex;
    flex-shrink: 0;

    @include breakpoint(small, medium) {
      display: none;
    }
  }

  &.placement-mobile {
    display: none;

    @include breakpoint(small, medium) {
      display: inline-flex;
      align-self: flex-start;
    }
  }
`;

const RowSkeleton = styled(Shimmer)<{ variant: PackPreviewVariant }>`
  width: 100%;

  &.variant-tabs {
    height: 72px;
    margin: var(--spacing-xs-16, 16px) 0;
  }

  &.variant-pickDownload {
    height: 58px;
  }
`;

// Bottom fade into the panel background with the "+N related research papers"
// count, per the comps. Absolutely positioned within the consumer's clipping
// container.
const FadeOut = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: var(--spacing-xs-4, 4px);
  height: 109px;
  padding-bottom: var(--spacing-xs-16, 16px);
  background: linear-gradient(180deg, rgba(238, 242, 249, 0) 13%, #eef2f9 56%);
`;

const FadeCount = styled.span`
  font-family:
    var(--type-font-family-sans-serif, 'DM Sans'), Helvetica, Arial, sans-serif;
  font-size: var(--type-body-lg-size, 20px);
  line-height: var(--type-body-lg-line-height, 32px);
  font-weight: var(--type-body-lg-bold-font-weight, 600);
  color: var(--text-primary, #141413);
`;

const FadeLabel = styled.span`
  font-family:
    var(--type-font-family-sans-serif, 'DM Sans'), Helvetica, Arial, sans-serif;
  font-size: var(--type-body-xs-size, 12px);
  line-height: var(--type-body-xs-line-height, 16px);
  color: var(--text-primary, #141413);
  // Baseline-align the small label with the big count per the comps.
  padding-bottom: 6px;
`;

export type PackPreviewRowsProps = {
  variant: PackPreviewVariant;
  relatedWorks: SidebarWork[];
  rowViews: Record<string, number>;
};

export const PackPreviewRows: React.FC<PackPreviewRowsProps> = ({
  variant,
  relatedWorks,
  rowViews,
}) => (
  <Rows variant={variant}>
    {relatedWorks.length
      ? relatedWorks.map((relatedWork, i) => {
          const relevancy = `${syntheticRelevancy(i)}% relevancy`;
          const rowAuthors = authorLine(relatedWork.authors);
          const rowViewCount = rowViews[String(relatedWork.id)];
          return (
            <Row key={relatedWork.id} variant={variant}>
              <RowThumb variant={variant} src={relatedWork.image} alt="" />
              <RowBody>
                <RelevancyBadge variant={variant} placement="mobile">
                  {relevancy}
                </RelevancyBadge>
                <RowTitle variant={variant}>{relatedWork.title}</RowTitle>
                {/* Segments drop out when their data is missing rather than
                    leaving dangling separators. */}
                <RowMeta>
                  {rowAuthors && (
                    <RowMetaAuthor>
                      <span>by {rowAuthors}</span>
                      {rowViewCount !== undefined && <span>|</span>}
                    </RowMetaAuthor>
                  )}
                  {rowViewCount !== undefined && (
                    <span>{formatStat(rowViewCount)} views</span>
                  )}
                </RowMeta>
              </RowBody>
              <RelevancyBadge variant={variant} placement="desktop">
                {relevancy}
              </RelevancyBadge>
            </Row>
          );
        })
      : [0, 1, 2].map((i) => <RowSkeleton key={i} variant={variant} />)}
  </Rows>
);

/** The "+N related research papers" fade; render inside the clipping box. */
export const PackPreviewFade: React.FC<{ relatedCount: number }> = ({
  relatedCount,
}) => (
  <FadeOut>
    <FadeCount>+{formatStat(relatedCount)}</FadeCount>
    <FadeLabel>related research papers</FadeLabel>
  </FadeOut>
);

export default PackPreviewRows;
